"use client";

import * as React from "react";

import { cn } from "@/lib/utils";

export type SettingRowProps = {
  /** Short, scannable title — uses foreground text weight-semibold. */
  label: React.ReactNode;
  /** One-line helper. Ends sentence punctuation in copy is fine. */
  description?: React.ReactNode;
  /**
   * Inline control rendered on the right side of the row (e.g. a Switch).
   * Keep it small — a tall control should use `children` instead.
   */
  trailing?: React.ReactNode;
  /**
   * Full-width control(s) rendered below the label block. Use for inputs,
   * selects, segmented toggles, or any control that needs its own row.
   */
  children?: React.ReactNode;
  /** Tiny status pill shown next to the label (Chip, Badge, etc). */
  badge?: React.ReactNode;
  className?: string;
  /** Disable the row visually (50% opacity, pointer-events off). */
  disabled?: boolean;
  /** Compact vertical padding — pair with dense lists. */
  dense?: boolean;
};

export function SettingRow({
  label,
  description,
  trailing,
  children,
  badge,
  className,
  disabled = false,
  dense = false,
}: SettingRowProps) {
  return (
    <div
      className={cn(
        "px-3 sm:px-4",
        dense ? "py-2.5" : "py-3.5",
        disabled && "pointer-events-none opacity-50",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-[13px] font-semibold text-foreground leading-tight">
              {label}
            </p>
            {badge ? <span className="shrink-0">{badge}</span> : null}
          </div>
          {description ? (
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {description}
            </p>
          ) : null}
        </div>
        {trailing ? (
          <div className="shrink-0 pt-0.5">{trailing}</div>
        ) : null}
      </div>
      {children ? <div className="mt-3">{children}</div> : null}
    </div>
  );
}
